import React from 'react';
import classnames from 'classnames';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import GlobalProps from '../../../components/globalProps';
import { Image } from '../../../model/asset';
import { ProductImage } from '../../../components/ProductImage/ProductImage';
import { LogoUploadButton } from './LogoUploadButton';
import { readBytesUrl } from '../../../util/util';

import './LogoUploader.css';
import { cloneDeep } from 'lodash';
import { t } from '../../../locales/translate';

export interface TempImage {
  id: string;
  encodedString: string;
  order: number;
  tags: string[];
  type: string;
}

interface Props extends GlobalProps {
  currentImages: Array<Image | TempImage>;
  label?: string;
  error?: boolean;
  onChange: (Images: Array<Image | TempImage>) => void;
}

export function LogoUploader({ currentImages, label, error, className, style, onChange }: Props): JSX.Element {
  function handleReorder(source: number, destination: number): void {
    const tempImages = cloneDeep(currentImages);
    const tempImage = tempImages.splice(source, 1)[0];
    tempImages.splice(destination, 0, tempImage);
    onChange(tempImages);
  }

  function handleRemove(index: number): void {
    const tempImages = cloneDeep(currentImages);
    tempImages.splice(index, 1);
    onChange(tempImages);
  }

  function handleSetPreview(index: number): void {
    const tempImages = cloneDeep(currentImages);
    const isPreview = tempImages[index].tags.includes('preview');
    if (isPreview) {
      tempImages[index].tags.splice(tempImages[index].tags.indexOf('preview'), 1);
    } else {
      tempImages[index].tags.push('preview');
    }
    onChange(tempImages);
  }

  function handleSetGallery(index: number): void {
    const tempImages = cloneDeep(currentImages);
    const isPreview = tempImages[index].tags.includes('gallery');
    if (isPreview) {
      tempImages[index].tags.splice(tempImages[index].tags.indexOf('gallery'), 1);
    } else {
      tempImages[index].tags.push('gallery');
    }
    onChange(tempImages);
  }

  function handleAdd(files: File[]): void {
    if (files.length > 0) {
      const imagePromises: Array<Promise<string>> = [];

      files.forEach((file) => {
        imagePromises.push(readBytesUrl(file, true));
      });

      Promise.all(imagePromises).then((res) => {
        onChange([
          ...currentImages,
          ...res.map((r) => {
            const parsedUrl = r.split(',');
            return {
              id: '',
              type: parsedUrl[0],
              encodedString: parsedUrl[1],
              order: currentImages.length,
              tags: [],
            };
          }),
        ]);
      });
    }
  }

  return (
    <div className={classnames('logo-uploader', className)} style={style}>
      <div className="logo-uploader__content">
        <div className="logo-uploader__label">{label}</div>
        <DragDropContext
          onDragEnd={(e) => {
            if (e.destination) {
              handleReorder(e.source.index, e.destination.index);
            }
          }}
        >
          <Droppable droppableId="dropzone" direction="horizontal">
            {(provided) => (
              <div ref={provided.innerRef} className="logo-uploader__images">
                {currentImages.map((image, index) => (
                  <DraggableImage
                    key={image.id}
                    image={image}
                    index={index}
                    onRemove={handleRemove}
                    setPreview={handleSetPreview}
                    setGallery={handleSetGallery}
                  />
                ))}
                {provided.placeholder}
                <div className="logo-uploader__overlay">
                  <LogoUploadButton onChange={handleAdd} multiple />
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {error && <div className="logo-uploader__error">{t.errors.uploadImage}</div>}
      </div>
    </div>
  );
}

function DraggableImage({
  image,
  index,
  onRemove,
  setPreview,
  setGallery,
}: {
  image?: Image | TempImage;
  index: number;
  onRemove: (index: number) => void;
  setPreview: (index: number) => void;
  setGallery: (index: number) => void;
}): JSX.Element {
  if (image) {
    return (
      <Draggable key={'PLU-Image-' + index} draggableId={'img' + index} index={index}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="draggable-tst"
          >
            <ProductImage
              imgSrc={
                Object.hasOwn(image, 'url')
                  ? (image as Image).url
                  : `data:${(image as TempImage).type};base64,${(image as TempImage).encodedString}`
              }
              originalSrc={Object.hasOwn(image, 'originalUrl') ? (image as Image).originalUrl : undefined}
              deleteImage={() => onRemove(index)}
              setPreview={() => setPreview(index)}
              isPreview={image.tags.includes('preview')}
              setGallery={() => setGallery(index)}
              isGallery={image.tags.includes('gallery')}
            />
          </div>
        )}
      </Draggable>
    );
  } else {
    return <div />;
  }
}
